"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const categories = [
{
  id: 1,
  title: '日用品',
  titleEn: 'Daily Necessities',
  desc: '清潔用品、個人護理、家居收納、文具辦公，覆蓋生活每個角落。',
  skus: '2,000+',
  img: "https://images.unsplash.com/photo-1677591276151-e11ed2e307c6",
  alt: 'Colorful household daily necessities products arranged neatly on white shelves',
  span: 'lg:col-span-2 lg:row-span-2',
  imgH: 'h-64 lg:h-80',
  accent: '#8B5E3C'
},
{
  id: 2,
  title: '母嬰用品',
  titleEn: 'Maternal & Baby',
  desc: '嬰兒護理、孕婦用品、兒童玩具，呵護每一個生命的起點。',
  skus: '800+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe375434-1772544713081.png",
  alt: 'Soft pastel baby products including bottles, toys and care items on light background',
  span: 'lg:col-span-1',
  imgH: 'h-48',
  accent: '#C4915A'
},
{
  id: 3,
  title: '小家電',
  titleEn: 'Small Appliances',
  desc: '電吹風、電飯鍋、榨汁機等生活小家電，提升家居品質。',
  skus: '500+',
  img: "https://images.unsplash.com/photo-1695427542120-c838836f5da0",
  alt: 'Modern small kitchen appliances including blender and coffee maker on counter',
  span: 'lg:col-span-1',
  imgH: 'h-48',
  accent: '#9C8270'
},
{
  id: 4,
  title: '廚房用具',
  titleEn: 'Kitchen Tools',
  desc: '鍋具、刀具、廚房小工具，為每一頓飯添加儀式感。',
  skus: '1,200+',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1abdc6cd8-1773033390472.png",
  alt: 'Professional kitchen tools including pots pans and cooking utensils arranged on wooden surface',
  span: 'lg:col-span-2',
  imgH: 'h-52',
  accent: '#8B5E3C'
}];


export default function CategoriesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef?.current?.querySelectorAll('.reveal-card');
    cards?.forEach((card) => {
      card?.classList?.add('reveal-hidden');
      observer?.observe(card);
    });

    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-white">
      <div className="max-w-container mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">
              PRODUCT CATEGORIES · 產品類別
            </p>
            <h2 className="font-heading font-bold text-brand-fg" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              四大核心品類
            </h2>
          </div>
          <Link
            href="/business-scope"
            className="flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all duration-200">
            
            查看完整業務範圍
            <span>→</span>
          </Link>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-auto">
          {categories?.map((cat, idx) =>
          <div
            key={cat?.id}
            className={`reveal-card bento-card rounded-3xl overflow-hidden border border-brand-border group cursor-pointer ${cat?.span}`}
            style={{ transitionDelay: `${idx * 80}ms` }}>
            
              <div className={`relative overflow-hidden ${cat?.imgH} bg-secondary`}>
                <AppImage
                src={cat?.img}
                alt={cat?.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ background: 'rgba(139,94,60,0.85)', backdropFilter: 'blur(8px)' }}>
                
                  {cat?.skus} SKU
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-heading font-semibold text-brand-fg text-xl">{cat?.title}</h3>
                    <p className="text-xs text-brand-muted mb-2">{cat?.titleEn}</p>
                  </div>
                </div>
                <p className="text-sm text-brand-muted leading-relaxed">{cat?.desc}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}